import { lazy } from "react";
import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { AuthGuard } from "@/components/AuthGuard";
import AdminLayout from "@/components/AdminLayout";
import AdminUsersLayout from "@/components/AdminUsersLayout";
import NotFound from "@/pages/NotFound";
import OnboardingPage from "@/pages/onboarding/page";
import LoginPage from "@/pages/login/page";
import AdminUsersPage from "@/pages/admin-users/page";
import AdminUserDetailPage from "@/pages/admin-users/detail";
import SystemConfigPage from "@/pages/config/system-config";

function createCryptoRoutes(): RouteObject[] {
  const CryptoLegacyRedirect = lazy(() => import("@/pages/crypto/LegacyRedirect"));
  const CryptoPage = lazy(() => import("@/pages/crypto/page"));
  const RetiredPage = lazy(() => import("@/pages/crypto/page").then(m => ({ default: m.RetiredCryptoPage })));
  return [
    { path: "/crypto", element: <Navigate to="/crypto/overview" replace /> },
    { path: "/crypto/*", element: <CryptoPage /> },
    { path: "/dashboard", element: <Navigate to="/crypto/overview" replace /> },
    { path: "/users", element: <Navigate to="/crypto/users" replace /> },
    { path: "/users/:id", element: <CryptoLegacyRedirect resource="users" /> },
    { path: "/agents", element: <Navigate to="/crypto/agents" replace /> },
    { path: "/agents/:id", element: <CryptoLegacyRedirect resource="agents" /> },
    { path: "/config-ai-models", element: <Navigate to="/crypto/models" replace /> },
    ...["settlements", "settlement-records", "deposits", "payment-addresses", "withdrawals", "withdrawal-history", "risk-dashboard", "audit-logs", "config-rates", "config-plans", "config-announcements", "refunds", "reconciliation", "revenue-ledger"].map(path => ({ path: `/${path}`, element: <RetiredPage /> })),
  ];
}

function createUsStockRoutes(): RouteObject[] {
  const UsStockPage = lazy(() => import("@/pages/us-stock/page"));
  return [{ path: "/us-stock", element: <UsStockPage /> }];
}

function createTianjiRoutes(): RouteObject[] {
  const TianjiPage = lazy(() => import("@/pages/tianji/page"));
  return [
    { path: "/tianji", element: <Navigate to="/tianji/overview" replace /> },
    ...["overview", "users", "orders", "transactions", "subscriptions", "notifications", "anomalies", "usage", "review-accounts", "runtime-configs", "audit-logs"].map((screen) => ({ path: `/tianji/${screen}`, element: <TianjiPage /> })),
  ];
}

function createAiRoutes(): RouteObject[] {
  const AiUsersPage = lazy(() => import("@/pages/ai/users/page"));
  const AiUserDetailPage = lazy(() => import("@/pages/ai/users/detail"));
  const AiGenerationsPage = lazy(() => import("@/pages/ai/generations/page"));
  const AiGenerationDetailPage = lazy(() => import("@/pages/ai/generations/detail"));
  const AiVipPackagesPage = lazy(() => import("@/pages/ai/vip/packages/page"));
  const AiVipSubscriptionsPage = lazy(() => import("@/pages/ai/vip/subscriptions/page"));
  const AiSubscriptionOrdersPage = lazy(() => import("@/pages/ai/subscription-orders/page"));
  const AiOrdersPage = lazy(() => import("@/pages/ai/orders/page"));
  const AiPointsLogPage = lazy(() => import("@/pages/ai/points-log/page"));
  const AiConfigPage = lazy(() => import("@/pages/ai/config/page"));
  const AiAuditImagesPage = lazy(() => import("@/pages/ai/audit/images/page"));
  const AiAuditPromptsPage = lazy(() => import("@/pages/ai/audit/prompts/page"));
  const AiAuditAgentTracePage = lazy(() => import("@/pages/ai/audit/agent-trace/page"));
  const AiUserBehaviorPage = lazy(() => import("@/pages/ai/users/behavior/page"));
  const AiReportsPage = lazy(() => import("@/pages/ai/reports/page"));

  return [
    { path: "/ai/dashboard", element: <Navigate to="/ai/reports" replace /> },
    { path: "/ai/users", element: <AiUsersPage /> },
    { path: "/ai/users/:id", element: <AiUserDetailPage /> },
    { path: "/ai/generations", element: <AiGenerationsPage /> },
    { path: "/ai/generations/:id", element: <AiGenerationDetailPage /> },
    { path: "/ai/vip/packages", element: <AiVipPackagesPage /> },
    { path: "/ai/vip/subscription-orders", element: <AiSubscriptionOrdersPage /> },
    { path: "/ai/vip/subscriptions", element: <AiVipSubscriptionsPage /> },
    { path: "/ai/orders", element: <AiOrdersPage /> },
    { path: "/ai/points-log", element: <AiPointsLogPage /> },
    { path: "/ai/config", element: <AiConfigPage /> },
    { path: "/ai/audit/images", element: <AiAuditImagesPage /> },
    { path: "/ai/audit/prompts", element: <AiAuditPromptsPage /> },
    { path: "/ai/audit/agent-trace", element: <AiAuditAgentTracePage /> },
    { path: "/ai/users/behavior", element: <AiUserBehaviorPage /> },
    { path: "/ai/reports", element: <AiReportsPage /> },
  ];
}

function createForexRoutes(): RouteObject[] {
  const ForexPage = lazy(() => import("@/pages/forex/page"));
  const ForexAgentDetailPage = lazy(() => import("@/pages/forex/components/ConfigAgentDetailPage"));
  const ForexMerchantDetailPage = lazy(() => import("@/pages/forex/components/MerchantDetailPage"));
  const ForexReviewDetailPage = lazy(() => import("@/pages/forex/components/ReviewDetailPage"));
  const ForexMasterDetailPage = lazy(() => import("@/pages/forex/components/MasterDetailPage"));
  const ForexSubAuditDetailPage = lazy(() => import("@/pages/forex/components/SubAuditDetailPage"));
  const ForexAccountCommandDetailPage = lazy(() => import("@/pages/forex/components/AccountCommandDetailPage"));
  const ForexModelLogsDetailPage = lazy(() => import("@/pages/forex/components/ModelLogsDetailPage"));
  const ForexExecutionDetailPage = lazy(() => import("@/pages/forex/components/ExecutionDetailPage"));
  const ForexConfigDetailPage = lazy(() => import("@/pages/forex/components/ConfigDetailPage"));
  const ForexComplianceDetailPage = lazy(() => import("@/pages/forex/components/ComplianceDetailPage"));
  const ForexDataSourcesPage = lazy(() => import("@/pages/forex/components/DataSourcesPage"));
  const ForexDataSourcesDetailPage = lazy(() => import("@/pages/forex/components/DataSourcesDetailPage"));
  const ForexFollowDetailPage = lazy(() => import("@/pages/forex/components/FollowDetailPage"));
  const ForexSubDetailPage = lazy(() => import("@/pages/forex/components/SubDetailPage"));
  const ForexTerminalPage = lazy(() => import("@/pages/forex/components/TerminalPage"));
  const ForexTerminalDetailPage = lazy(() => import("@/pages/forex/components/TerminalDetailPage"));
  const ForexTerminalCreatePage = lazy(() => import("@/pages/forex/components/TerminalCreatePage"));
  const ForexSubDetailBindPage = lazy(() => import("@/pages/forex/components/SubDetailBindPage"));
  const ForexRiskConfigDetailPage = lazy(() => import("@/pages/forex/components/RiskConfigDetailPage"));
  const ForexKillSwitchDetailPage = lazy(() => import("@/pages/forex/components/KillSwitchDetailPage"));
  const ForexQueryPolicyDetailPage = lazy(() => import("@/pages/forex/components/QueryPolicyDetailPage"));
  const ForexMasterBindPage = lazy(() => import("@/pages/forex/components/MasterBindPage"));
  const ForexTerminalAnomalyPage = lazy(() => import("@/pages/forex/components/TerminalAnomalyPage"));
  const ForexConfigAgentCreatePage = lazy(() => import("@/pages/forex/components/ConfigAgentCreatePage"));
  const ForexConfigStrategyTemplatePage = lazy(() => import("@/pages/forex/components/ConfigStrategyTemplatePage"));
  const ForexModelDetailPage = lazy(() => import("@/pages/forex/components/ModelDetailPage"));
  const ForexMasterCreatePage = lazy(() => import("@/pages/forex/components/MasterCreatePage"));
  const ForexModelConfigPage = lazy(() => import("@/pages/forex/components/ForexModelConfigPage"));
  const ForexSymbolConfigDetailPage = lazy(() => import("@/pages/forex/components/SymbolConfigDetailPage"));
  const ForexConfigReleasesPage = lazy(() => import("@/pages/forex/components/ConfigReleasesPage"));
  const ForexConfigReleasesDetailPage = lazy(() => import("@/pages/forex/components/ConfigReleasesDetailPage"));
  const ProfitDetailPage = lazy(() => import("@/pages/forex/components/ProfitDetailPage"));
  const ProfitDashboardPage = lazy(() => import("@/pages/forex/ProfitDashboardPage"));
  const ModelDashboardPage = lazy(() => import("@/pages/forex/ModelDashboardPage"));
  const ExceptionsPage = lazy(() => import("@/pages/forex/ExceptionsPage"));
  const AgentsAuditPage = lazy(() => import("@/pages/forex/AgentsAuditPage"));
  const CollectorsPage = lazy(() => import("@/pages/forex/CollectorsPage"));
  const MasterAccountsPage = lazy(() => import("@/pages/forex/MasterAccountsPage"));
  const MasterAccountsBindPage = lazy(() => import("@/pages/forex/MasterAccountsBindPage"));
  const TerminalAnomaliesPage = lazy(() => import("@/pages/forex/TerminalAnomaliesPage"));
  const SubAccountsPage = lazy(() => import("@/pages/forex/SubAccountsPage"));
  const SubAccountsDetailPage = lazy(() => import("@/pages/forex/SubAccountsDetailPage"));
  const MerchantsPage = lazy(() => import("@/pages/forex/MerchantsPage"));

  return [
    { path: "/forex", element: <Navigate to="/forex/overview" replace /> },
    { path: "/forex/overview", element: <ForexPage /> },
    { path: "/forex/performance", element: <ForexPage /> },
    { path: "/forex/model-usage", element: <ForexPage /> },
    { path: "/forex/anomalies", element: <ForexPage /> },
    { path: "/forex/agents/detail", element: <ForexAgentDetailPage /> },
    { path: "/forex/agents/review/detail", element: <ForexReviewDetailPage /> },
    { path: "/forex/master/detail", element: <ForexMasterDetailPage /> },
    { path: "/forex/master/bind", element: <ForexPage /> },
    { path: "/forex/master/add", element: <Navigate to="/forex/master/bind" replace /> },
    { path: "/forex/merchants/detail", element: <ForexMerchantDetailPage /> },
    { path: "/forex/terminal/create", element: <ForexTerminalCreatePage /> },
    { path: "/forex/terminal/detail", element: <ForexTerminalDetailPage /> },
    { path: "/forex/sub-audit/detail", element: <ForexSubAuditDetailPage /> },
    { path: "/forex/sub-detail/bind", element: <ForexSubDetailBindPage /> },
    { path: "/forex/sub-detail/detail", element: <ForexSubDetailPage /> },
    { path: "/forex/follow/detail", element: <ForexFollowDetailPage /> },
    { path: "/forex/account-commands/detail", element: <ForexAccountCommandDetailPage /> },
    { path: "/forex/execution-detail/detail", element: <ForexExecutionDetailPage /> },
    { path: "/forex/config/detail", element: <ForexConfigDetailPage /> },
    { path: "/forex/compliance/detail", element: <ForexComplianceDetailPage /> },
    { path: "/forex/model-logs/detail", element: <ForexModelLogsDetailPage /> },
    { path: "/forex/data-sources/detail", element: <ForexDataSourcesDetailPage /> },
    { path: "/forex/risk-config/detail", element: <ForexRiskConfigDetailPage /> },
    { path: "/forex/kill-switch/detail", element: <ForexKillSwitchDetailPage /> },
    { path: "/forex/query-policy/detail", element: <ForexQueryPolicyDetailPage /> },
    { path: "/forex/master-bind", element: <ForexMasterBindPage /> },
    { path: "/forex/master/create", element: <ForexMasterCreatePage /> },
    { path: "/forex/terminal-anomaly", element: <ForexTerminalAnomalyPage /> },
    { path: "/forex/strategy-templates", element: <ForexConfigStrategyTemplatePage /> },
    { path: "/forex/strategy-template/detail", element: <Navigate to="/forex/strategy-templates" replace /> },
    { path: "/forex/agents/model-config/detail", element: <ForexModelDetailPage /> },
    { path: "/forex/symbol-config/detail", element: <ForexSymbolConfigDetailPage /> },
    { path: "/forex/config/releases", element: <ForexConfigReleasesPage /> },
    { path: "/forex/config/releases/detail", element: <ForexConfigReleasesDetailPage /> },
    { path: "/forex/profit-dashboard/agent-detail", element: <ProfitDetailPage /> },
    { path: "/forex/profit-dashboard/merchant-detail", element: <ProfitDetailPage /> },
    { path: "/forex/profit-dashboard/master-account-detail", element: <ProfitDetailPage /> },
    { path: "/forex/profit-dashboard", element: <ProfitDashboardPage /> },
    { path: "/forex/model-dashboard", element: <ModelDashboardPage /> },
    { path: "/forex/exceptions", element: <ExceptionsPage /> },
    { path: "/forex/agents/audit", element: <AgentsAuditPage /> },
    { path: "/forex/master-accounts", element: <MasterAccountsPage /> },
    { path: "/forex/master-accounts/bind", element: <MasterAccountsBindPage /> },
    { path: "/forex/terminal-anomalies", element: <TerminalAnomaliesPage /> },
    { path: "/forex/sub-accounts", element: <SubAccountsPage /> },
    { path: "/forex/sub-accounts/detail", element: <SubAccountsDetailPage /> },
    { path: "/forex/merchants", element: <MerchantsPage /> },
    { path: "/forex/agents/create", element: <ForexConfigAgentCreatePage /> },
    { path: "/forex/agents/review", element: <ForexPage /> },
    { path: "/forex/agents/model-config", element: <Navigate to="/forex/model-config" replace /> },
    { path: "/forex/agents", element: <ForexPage /> },
    { path: "/forex/model-config", element: <ForexModelConfigPage /> },
    { path: "/forex/master", element: <ForexPage /> },
    { path: "/forex/terminal", element: <ForexTerminalPage /> },
    { path: "/forex/terminals", element: <Navigate to="/forex/terminal" replace /> },
    { path: "/forex/terminal/anomalies", element: <TerminalAnomaliesPage /> },
    { path: "/forex/sub-audit", element: <Navigate to="/forex/sub-accounts" replace /> },
    { path: "/forex/sub-detail", element: <Navigate to="/forex/sub-accounts" replace /> },
    { path: "/forex/follow", element: <ForexPage /> },
    { path: "/forex/follow-detail", element: <Navigate to="/forex/follow" replace /> },
    { path: "/forex/account-commands", element: <ForexPage /> },
    { path: "/forex/execution-detail", element: <ForexPage /> },
    { path: "/forex/account-execution-issues", element: <ForexPage /> },
    { path: "/forex/risk-config", element: <ForexPage /> },
    { path: "/forex/kill-switch", element: <ForexPage /> },
    { path: "/forex/config", element: <ForexPage /> },
    { path: "/forex/compliance", element: <ForexPage /> },
    { path: "/forex/query-policy", element: <ForexPage /> },
    { path: "/forex/bc-loop", element: <ForexPage /> },
    { path: "/forex/collectors", element: <CollectorsPage /> },
    { path: "/forex/data-sources", element: <ForexDataSourcesPage /> },
    { path: "/forex/admin-users", element: <ForexPage /> },
    { path: "/forex/roles", element: <ForexPage /> },
    { path: "/forex/audit", element: <ForexPage /> },
    { path: "/forex/security", element: <ForexPage /> },
    { path: "/forex/model-logs", element: <ForexPage /> },
  ];
}

const domainRoutes: RouteObject[] = [];

if (__DOMAIN_CRYPTO__) {
  domainRoutes.push(...createCryptoRoutes());
}

if (__DOMAIN_US_STOCK__) {
  domainRoutes.push(...createUsStockRoutes());
}

if (__DOMAIN_FOREX__) {
  domainRoutes.push(...createForexRoutes());
}

if (__DOMAIN_AI__) {
  domainRoutes.push(...createAiRoutes());
}

if (__DOMAIN_TIANJI__) {
  domainRoutes.push(...createTianjiRoutes());
}

const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  {
    element: <AuthGuard />,
    children: [
      { path: "/", element: <OnboardingPage /> },
      {
        element: <AdminUsersLayout />,
        children: [
          { path: "/admin-users", element: <AdminUsersPage /> },
          { path: "/admin-users/:id", element: <AdminUserDetailPage /> },
          { path: "/system-config", element: <SystemConfigPage /> },
        ],
      },
      {
        element: <AdminLayout />,
        children: domainRoutes,
      },
    ],
  },
  { path: "*", element: <NotFound /> },
];

export default routes;
